import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateLabel as updateLabelRequest } from '@/lib/api/generated/api';
import type { LabelColor, LabelView } from '@/lib/api/generated/model';
import { workspaceKeys } from '@/features/workspaces';
import { CreateLabelError, classifyUpdateLabelFailure } from '../errors';

export interface UpdateLabelInput {
  workspaceId: string;
  labelId: string;
  /** Omitted fields are left as they are — this is a patch, not a replacement. */
  name?: string;
  color?: LabelColor;
}

/**
 * Rename a label, or move it to another colour slot.
 *
 * Invalidates the aggregate rather than patching it. A rename reaches every
 * task carrying the label, every chip on every card and the picker's registry
 * at once — there is no single cached entity to patch, and this is a
 * once-in-a-while act on a settings page rather than something happening
 * behind a two-second poll, so the extra read costs nothing anyone can feel.
 * That is the opposite trade from `useReplaceTaskLabels`, deliberately.
 *
 * A colliding name rejects with the same `CreateLabelError` a create does: the
 * backend refuses both with `LABEL_NAME_CONFLICT`, the reader's answer is the
 * same in both cases — type a different word — and two error types for one
 * refusal would mean the settings surface had to learn a second one.
 */
export function useUpdateLabel() {
  const queryClient = useQueryClient();

  return useMutation<LabelView, Error, UpdateLabelInput>({
    mutationFn: async ({ workspaceId, labelId, name, color }) => {
      const response = await updateLabelRequest(workspaceId, labelId, {
        ...(name === undefined ? {} : { name }),
        ...(color === undefined ? {} : { color }),
      });
      if (response.status !== 200) {
        throw new CreateLabelError(classifyUpdateLabelFailure(response.data));
      }
      return response.data;
    },
    onSuccess: (_label, { workspaceId }) => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
    },
  });
}
